import React, { useEffect, useState, useCallback } from "react";
import PhoneInput from "react-phone-input-2";

import { Button } from "react-bootstrap";
import { AboutVanueTypes } from "../../Constants/Interfaces";

import TagsInputList from "../../Components/TagsInputList";
import CustomButton from "../../Components/Button";
import AddressInput from "../../Components/AddressInput";
import {
  isValidEmail,
  isValidPhone,
  normalizeUsPhone,
  showToast,
} from "../../utils/helpers";

import { API } from "../../utils/Apis";
import { saveLocalData } from "../../utils/LocalStorage";
import { VenueAgent } from "../../Components/Agent/VenueAgent";
import { architectTypes, initVanueData } from "../../Constants";

const AboutVanue: React.FC<AboutVanueTypes> = ({
  vanue,
  changeNextStep,
  setVenueInfo,
  skipStep,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    ...initVanueData,
  });
  const [errors, setErrors] = useState({
    name: "",
    type: "",
    email: "",
    phone: "",
    description: "",
    address: "",
    spaces: "",
    area: "",
    architectTags: [],
  });
  const [vanueId, setVanueID] = useState("");

  const onChange = (field: string, value: string | string[]) => {
    setData({
      ...data,
      [field]: value,
    });
  };

  const validateForm = () => {
    let vanueErr = false;
    const err = {
      name: "",
      address: "",
      email: "",
      phone: "",
      spaces: "",
      area: "",
      architectTags: "",
      description: "",
    };

    if (!data.name || data.name === "") {
      err.name = "Please enter the venue name.";
      vanueErr = true;
    }

    if (!data.address || data.address === "") {
      err.address = "Please enter the venue address.";
      vanueErr = true;
    }

    if (!data.email || data.email === "") {
      err.email = "Please enter the venue email.";
      vanueErr = true;
    } else if (data.email && !isValidEmail(data.email)) {
      err.email = "Please enter a valid email address.";
      vanueErr = true;
    }

    if (!data.phone || data.phone === "") {
      err.phone = "Please enter a phone number.";
      vanueErr = true;
    } else if (data.phone && !isValidPhone(data.phone)) {
      err.phone = "Please enter a valid phone number.";
      vanueErr = true;
    }

    if (!data.totalSpaces || data.totalSpaces === "") {
      err.spaces = "Please enter the number of spaces your venue has.";
      vanueErr = true;
    }

    if (!data.description || data.description === "") {
      err.description = "Please enter a description for the venue.";
      vanueErr = true;
    }

    setErrors(err);

    return vanueErr;
  };

  const saveVanue = () => {
    const isError = validateForm();

    if (!isError && !loading) {
      setLoading(true);

      if (!vanueId || vanueId === "") {
        API.createNewVanue(data)
          .then((res) => {
            if (res.type === "success") {
              setVenueInfo(res.data);
              setVanueID(res.data.id);
              saveLocalData("onBoardingVanueID", res.data.id);

              changeNextStep();

              showToast("success", res.message);
            } else {
              showToast("error", res.message);
            }
          })
          .catch((err) => {
            showToast("error", err.message);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        const tempData = { ...data };
        if (data.architectTags.length === 0) {
          delete tempData["architectTags"];
        } else {
          const architectTags =
            data.architectTags?.map((tag: any) => tag.text) || [];

          if (architectTags.length > 0) {
            tempData.architectTags = architectTags;
          }
        }

        API.updateVanue(tempData)
          .then((res) => {
            if (res.type === "success") {
              changeNextStep();

              showToast("success", res.message);
            } else {
              showToast("error", res.message);
            }
          })
          .catch((err) => {
            showToast("error", err.message);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }
  };

  const handleVenueCreated = useCallback(
    (venue: any) => {
      console.log("Venue created by agent:", venue);

      const architectTags = venue.tags
        ? venue.tags
            .filter((tag: any) => tag.type === "architect")
            .map((tag: any) => tag.text)
        : [];

      const venueData = {
        id: venue.id,
        name: venue.name === "" ? null : venue.name,
        address: venue.address === "" ? null : venue.address,
        email: venue.email === "" ? null : venue.email,
        phone: venue.phone === "" ? null : venue.phone,
        totalSpaces:
          venue.totalSpaces?.toString() === ""
            ? null
            : venue.totalSpaces?.toString(),
        area: venue.area?.toString() === "" ? null : venue.area?.toString(),
        description: venue.description === "" ? null : venue.description,
        architectTags: architectTags,
      };

      setData(venueData);
      setVanueID(venue.id);
      setVenueInfo(venue);
      saveLocalData("onBoardingVanueID", venue.id);

      showToast("success", "Venue created successfully via AI Agent!");
    },
    [setVenueInfo]
  );

  const handleVenueUpdated = useCallback(
    (venue: any) => {
      console.log("Venue updated by agent:", venue);

      const architectTags = venue.tags
        ? venue.tags
            .filter((tag: any) => tag.type === "architect")
            .map((tag: any) => tag.text)
        : [];

      const venueData = {
        id: venue.id,
        name: venue.name === "" ? null : venue.name,
        address: venue.address === "" ? null : venue.address,
        email: venue.email === "" ? null : venue.email,
        phone: venue.phone === "" ? null : venue.phone,
        totalSpaces:
          venue.totalSpaces?.toString() === ""
            ? null
            : venue.totalSpaces?.toString(),
        area: venue.area?.toString() === "" ? null : venue.area?.toString(),
        description: venue.description === "" ? null : venue.description,
        architectTags: architectTags,
      };

      setData(venueData);
      setVenueInfo(venue);

      showToast("success", "Venue updated successfully via AI Agent!");
    },
    [setVenueInfo]
  );

  useEffect(() => {
    if (vanue) {
      // Extract architect tags from the tags array if present
      const architectTags =
        vanue && vanue.tags
          ? vanue.tags.filter((item, index) => item.type === "architect")
          : [];

      setData({
        ...vanue,
        architectTags: architectTags,
      });

      setVanueID(vanue.id);
    }
  }, [vanue]);

  return (
    <>
      <div className="steps-headings">
        <h2>Tell us about your venue</h2>

        <div className="steps-sub-heading">
          <h3>General Information</h3>
        </div>

        <p className="required-note">
          <span className="required-star">*</span> Required
        </p>

        <div className="mt-4">
          <VenueAgent
            venueId={vanueId}
            onVenueCreated={handleVenueCreated}
            onVenueUpdated={handleVenueUpdated}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 col-sm-6 col-xs-12">
          <div className="form-group">
            <label>
              Venue name<span className="required-star">*</span>
            </label>
            <input
              type="text"
              name="name"
              className="form-control"
              value={data.name}
              onChange={(e) => onChange("name", e.target.value)}
            />
            {errors.name && <span className="error">{errors.name}</span>}
          </div>
        </div>
        <div className="col-md-6 col-sm-6 col-xs-12">
          <div className="form-group">
            <label>
              Venue address<span className="required-star">*</span>
            </label>
            <AddressInput
              type="fullObject"
              requireSelection
              placeHolder="Start typing the venue address…"
              address={data.address}
              onChange={(address: any) => {
                if (typeof address === "object") {
                  setData((prev: any) => ({
                    ...prev,
                    ...address,
                    zipcode: address.postalCode,
                  }));
                } else {
                  onChange("address", address);
                }
              }}
            />
            {errors.address && <span className="error">{errors.address}</span>}
          </div>
        </div>
        <div className="col-md-6 col-sm-6 col-xs-12">
          <div className="form-group">
            <label>
              Email<span className="required-star">*</span>
            </label>
            <input
              type="text"
              name="email"
              className="form-control"
              value={data.email}
              onChange={(e) => onChange("email", e.target.value)}
            />
            {errors.email && <span className="error">{errors.email}</span>}
          </div>
        </div>
        <div className="col-md-6 col-sm-6 col-xs-12">
          <div className="form-group">
            <label>
              Phone<span className="required-star">*</span>
            </label>

            <PhoneInput
              country={"us"}
              onlyCountries={["us"]}
              preferredCountries={["us"]}
              value={data.phone}
              onChange={(phone) => onChange("phone", normalizeUsPhone(phone))}
              containerClass="form-control form-phone-field"
            />

            {errors.phone && <span className="error">{errors.phone}</span>}
          </div>
        </div>
        <div className="col-md-6 col-sm-6 col-xs-12">
          <div className="form-group">
            <label>
              Number of spaces<span className="required-star">*</span>
            </label>
            <input
              type="text"
              name="totalSpaces"
              className="form-control"
              value={data.totalSpaces}
              onChange={(e) => onChange("totalSpaces", e.target.value)}
            />

            {errors.spaces && <span className="error">{errors.spaces}</span>}
          </div>
        </div>

        <div className="col-md-6 col-sm-6 col-xs-12">
          <div className="form-group">
            <label>Area (sq ft / sq m)</label>
            <input
              type="text"
              name="area"
              className="form-control"
              value={data.area}
              onChange={(e) => onChange("area", e.target.value)}
              placeholder="e.g., 5000"
            />

            {errors.area && <span className="error">{errors.area}</span>}
          </div>
        </div>

        <TagsInputList
          systemItems={architectTypes}
          type="Venue"
          field="architect"
          items={data.architectTags}
          label="Architecture"
          updateTags={(items: Array<string>) =>
            onChange("architectTags", items)
          }
          error={errors.architectTags}
        />

        <div className="col-md-12 col-sm-12 col-xs-12">
          <div className="form-group">
            <label>
              Description<span className="required-star">*</span>
            </label>
            <textarea
              rows={5}
              name="description"
              className="form-control"
              value={data.description}
              onChange={(e) => onChange("description", e.target.value)}
            />
            {errors.description && (
              <span className="error">{errors.description}</span>
            )}
          </div>
        </div>
      </div>

      <div className="navigation-btns">
        <Button className="skip-btn" onClick={() => skipStep()}>
          Skip
        </Button>

        <CustomButton
          isLoading={loading}
          title="Continue"
          onClick={() => saveVanue()}
        />
      </div>
    </>
  );
};

export default AboutVanue;
